import {
  ACQUISITION_COOKIE,
  ACQUISITION_COOKIE_DAYS,
  ACQUISITION_KEYS,
  ACQUISITION_VALUE_MAX,
} from "@listou/shared/constants";

/**
 * Remembers where a visitor came from until they sign up; the server reads it once, when
 * the account is created (apps/server/src/auth/acquisition.ts). Each field keeps its first
 * value: the list that brought someone stays theirs even if they open three more.
 * Browser only: call it from an effect or an event, never during render.
 */

type Field = keyof typeof ACQUISITION_KEYS;

const read = (): URLSearchParams => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${ACQUISITION_COOKIE}=([^;]*)`));
  return new URLSearchParams(match ? decodeURIComponent(match[1] ?? "") : "");
};

const remember = (fields: Partial<Record<Field, string | null>>) => {
  const params = read();
  let changed = false;
  for (const [field, value] of Object.entries(fields) as [Field, string | null][]) {
    const key = ACQUISITION_KEYS[field];
    if (!value || params.has(key)) continue;
    params.set(key, value.slice(0, ACQUISITION_VALUE_MAX));
    changed = true;
  }
  if (!changed) return;
  const maxAge = ACQUISITION_COOKIE_DAYS * 24 * 60 * 60;
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${ACQUISITION_COOKIE}=${encodeURIComponent(params.toString())}; Path=/; Max-Age=${maxAge}; SameSite=Lax${secure}`;
};

/** A search engine or a social network, from its host: "www.google.fr" → "google.fr". */
const referringSite = (): string | null => {
  try {
    const host = new URL(document.referrer).hostname.replace(/^www\./, "");
    return host && host !== window.location.hostname ? host : null;
  } catch {
    return null;
  }
};

/** On arrival: the utm link, or else the site that sent the visitor. */
export const rememberArrival = () => {
  const search = new URLSearchParams(window.location.search);
  remember({
    source: search.get("utm_source") ?? referringSite(),
    campaign: search.get("utm_campaign"),
  });
};

export const rememberSharedList = (slug: string) => remember({ list: slug });
export const rememberSharedGroup = (slug: string) => remember({ group: slug });
